import React, { useEffect } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { Button, Icon } from '../../../components/common';
import { useNavigate } from 'react-router-dom';
import { ActivityLogEntry } from '../../activity-log/types';

const entityIcons: Record<string, string> = {
    prop: 'description',
    costume: 'checkroom',
    blocking: 'map',
    schedule: 'calendar_today',
    act: 'theater_comedy',
    scene: 'theater_comedy',
    character: 'group'
};

function formatRelativeTime(timestamp: number): string {
    const diffMs = Date.now() - timestamp;
    const diffMins = Math.floor(diffMs / 60_000);
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(timestamp).toLocaleDateString();
}

export const ActivitySummaryWidget: React.FC = () => {
    const projectId = useAppStore(state => state.cloud.currentProjectId);
    const activityLog = useAppStore(state => state.activityLog);
    const isLoading = useAppStore(state => state.isLoadingActivity);
    const fetchActivityLog = useAppStore(state => state.activityLogActions.fetchActivityLog);
    const navigate = useNavigate();

    useEffect(() => {
        if (projectId) {
            void fetchActivityLog(projectId, { limit: 5 });
        }
    }, [projectId, fetchActivityLog]);

    const recentEntries = activityLog.slice(0, 5);

    // Empty state
    if (!isLoading && recentEntries.length === 0) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Icon name="schedule" className="text-primary" />
                    Recent Activity
                </h3>
                <div className="text-center py-6">
                    <Icon name="schedule" className="text-5xl text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No activity yet</p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">Changes to your production will appear here</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Icon name="schedule" className="text-primary" />
                Recent Activity
            </h3>

            {/* Loading skeleton */}
            {isLoading ? <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="animate-pulse flex items-start gap-3">
                            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0" />
                            <div className="flex-1 space-y-1.5">
                                <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                                <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2" />
                            </div>
                        </div>
                    ))}
                </div> : null}

            {/* Activity entries */}
            {!isLoading && (
                <div className="space-y-1">
                    {recentEntries.map((entry: ActivityLogEntry) => {
                        const iconName = entityIcons[entry.entityType] || 'description';

                        return (
                            <div
                                key={entry.id}
                                className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                            >
                                <div className="shrink-0 w-8 h-8 bg-prussian-50 dark:bg-prussian-900/30 rounded-full flex items-center justify-center mt-0.5">
                                    <Icon name={iconName} className="text-base text-prussian-600 dark:text-prussian-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {entry.summary}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {entry.userName}
                                        </span>
                                        <span className="text-gray-300 dark:text-gray-600">·</span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                            {formatRelativeTime(entry.timestamp)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* "View all" footer */}
            <Button
                onClick={() => { void navigate('/activity-log'); }}
                variant="plain"
                size="none"
                className="w-full mt-3 pt-3 border-t border-gray-100 dark:border-border-dark text-xs text-primary hover:text-primary/80 transition-colors text-center cursor-pointer"
            >
                View all activity →
            </Button>
        </div>
    );
};
